import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Image as ImageIcon,
  Music,
  Code2,
  MessageSquare,
  Sparkles,
  ArrowRight,
  Check,
  Zap,
  Shield,
  History,
  Wand2,
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Button from '../components/ui/Button';
import GlassCard from '../components/ui/GlassCard';
import Logo from '../components/ui/Logo';
import Reveal, { StaggerGroup, staggerItem } from '../components/ui/Reveal';
import { TIERS } from '../lib/constants';
// FX kit
import Aurora from '../components/fx/Aurora';
import Particles from '../components/fx/Particles';
import SplitText from '../components/fx/SplitText';
import RotatingText from '../components/fx/RotatingText';
import Magnetic from '../components/fx/Magnetic';
import Tilt from '../components/fx/Tilt';
import Marquee from '../components/fx/Marquee';
import CountUp from '../components/fx/CountUp';
import ScrollProgress from '../components/fx/ScrollProgress';

const STUDIOS = [
  { icon: ImageIcon, title: 'Image', blurb: 'FLUX.1, SDXL & SD 1.5. Prompt, pick a ratio, watch it resolve.', accent: 'from-brand-500/30 to-brand-500/0', color: 'text-brand-300' },
  { icon: Music, title: 'Music', blurb: 'MusicGen turns a sentence into a soundtrack with a live waveform.', accent: 'from-accent-cyan/30 to-accent-cyan/0', color: 'text-accent-cyan' },
  { icon: Code2, title: 'Code', blurb: 'StarCoder2 & Code Llama. Syntax-highlighted, one-click copy.', accent: 'from-accent-fuchsia/30 to-accent-fuchsia/0', color: 'text-accent-fuchsia' },
  { icon: MessageSquare, title: 'Chat', blurb: 'A fast AI assistant that streams, remembers, and speaks.', accent: 'from-accent-amber/30 to-accent-amber/0', color: 'text-accent-amber' },
];

const FEATURES = [
  { icon: Zap, title: 'Streaming generation', text: 'WebSocket streaming means tokens and previews land the instant they exist.' },
  { icon: Shield, title: 'Secure by default', text: 'JWT access + refresh tokens in httpOnly cookies. Limits enforced server-side.' },
  { icon: History, title: 'Everything is saved', text: 'Search, pin, export, and revisit every generation across all four studios.' },
  { icon: Wand2, title: 'Swap models freely', text: 'A model selector on every page — compare open-source models in a click.' },
];

const MODELS = [
  'FLUX.1-schnell', 'stable-diffusion-xl', 'stable-diffusion-1.5',
  'musicgen-small', 'starcoder2-3b', 'codellama-7b', 'claude-sonnet-4-6',
];

const STATS = [
  { value: 6, suffix: '', label: 'open models' },
  { value: 4, suffix: '', label: 'creative studios' },
  { value: 100, suffix: '%', label: 'open source' },
  { value: 0, prefix: '$', label: 'to get started' },
];

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <ScrollProgress />
      <Navbar />

      {/* ---------------- Hero ---------------- */}
      <section className="relative flex min-h-screen items-center justify-center px-4 pt-28 pb-20">
        <Aurora className="z-0" intensity={0.7} />
        <Particles className="z-0" count={130} />
        <div className="absolute inset-0 z-0 grid-bg opacity-60" aria-hidden />

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-content-muted backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-400" />
            </span>
            One prompt, infinite creation
          </motion.div>

          <h1 className="text-balance font-display text-5xl font-semibold leading-[1.04] tracking-tight sm:text-6xl md:text-7xl">
            <SplitText text="Turn a sentence into" className="block" stagger={0.045} />
            <span className="mt-1 block">
              <RotatingText
                words={['an image.', 'a soundtrack.', 'working code.', 'anything.']}
                className="text-flow"
              />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mx-auto mt-7 max-w-2xl text-balance text-lg text-slate-300"
          >
            Aether unifies open-source image, music, and code models with a fast AI
            assistant — beautifully fast, fully yours.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Magnetic strength={0.5}>
              <Link to="/signup">
                <Button size="lg" iconRight={ArrowRight}>
                  Start creating free
                </Button>
              </Link>
            </Magnetic>
            <Magnetic strength={0.4}>
              <a href="#studios">
                <Button size="lg" variant="secondary">
                  Explore the studios
                </Button>
              </a>
            </Magnetic>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="mx-auto mt-14 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4"
          >
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="font-display text-3xl font-semibold text-content">
                  <CountUp value={s.value} prefix={s.prefix || ''} suffix={s.suffix || ''} />
                </div>
                <div className="mt-1 text-xs text-content-subtle">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ---------------- Model marquee ---------------- */}
      <section className="relative border-y border-border/50 py-6">
        <Marquee>
          {MODELS.map((m) => (
            <span key={m} className="flex items-center gap-2 text-sm text-content-subtle">
              <Sparkles className="h-3.5 w-3.5 text-brand-300/70" />
              <span className="font-mono">{m}</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* ---------------- Features ---------------- */}
      <section id="features" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <SplitText
            as="h2"
            text="Built like a product, not a demo"
            inView
            by="word"
            className="font-display text-3xl font-semibold tracking-tight sm:text-4xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-4 text-content-muted">
              Real streaming, real persistence, real auth and billing. The polish is the point.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <motion.div key={f.title} variants={staggerItem}>
              <Tilt max={8}>
                <GlassCard hoverable className="h-full p-6">
                  <div className="mb-4 inline-flex rounded-xl bg-brand-500/15 p-2.5">
                    <f.icon className="h-5 w-5 text-brand-300" />
                  </div>
                  <h3 className="text-base font-semibold text-content">{f.title}</h3>
                  <p className="mt-2 text-sm text-content-muted">{f.text}</p>
                </GlassCard>
              </Tilt>
            </motion.div>
          ))}
        </StaggerGroup>
      </section>

      {/* ---------------- Studios ---------------- */}
      <section id="studios" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <SplitText
            as="h2"
            text="Four studios, one canvas"
            inView
            className="font-display text-3xl font-semibold tracking-tight sm:text-4xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-4 text-content-muted">
              Switch modalities without switching tools. Each studio is purpose-built and pixel-polished.
            </p>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-5 md:grid-cols-2">
          {STUDIOS.map((s) => (
            <motion.div key={s.title} variants={staggerItem}>
              <Tilt max={9} className="h-full">
                <GlassCard hoverable className="group relative h-full overflow-hidden p-7">
                  <div className={`absolute -right-12 -top-12 h-44 w-44 rounded-full bg-gradient-to-br ${s.accent} blur-2xl`} />
                  <div className="relative">
                    <s.icon className={`h-8 w-8 ${s.color}`} />
                    <h3 className="mt-5 font-display text-2xl font-semibold">{s.title} studio</h3>
                    <p className="mt-2 max-w-sm text-content-muted">{s.blurb}</p>
                    <Link
                      to="/signup"
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand-300 transition-colors hover:text-brand-200"
                    >
                      Try it <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </GlassCard>
              </Tilt>
            </motion.div>
          ))}
        </StaggerGroup>
      </section>

      {/* ---------------- Pricing teaser ---------------- */}
      <section id="pricing" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <SplitText
            as="h2"
            text="Start free. Upgrade when you fly."
            inView
            className="font-display text-3xl font-semibold tracking-tight sm:text-4xl"
          />
          <Reveal delay={0.1}>
            <p className="mt-4 text-content-muted">Transparent tiers with limits enforced fairly, server-side.</p>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-5 lg:grid-cols-3">
          {TIERS.map((tier) => (
            <motion.div key={tier.id} variants={staggerItem}>
              <GlassCard
                hoverable
                beam={tier.highlighted}
                className={`relative h-full p-7 ${tier.highlighted ? 'shadow-glow' : ''}`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-3 left-7 rounded-full bg-brand-500 px-3 py-1 text-xs font-semibold text-white">
                    Most popular
                  </span>
                )}
                <h3 className="text-lg font-semibold">{tier.name}</h3>
                <p className="text-sm text-content-subtle">{tier.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-4xl font-semibold">
                    $<CountUp value={tier.price} />
                  </span>
                  <span className="mb-1 text-sm text-content-subtle">/mo</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-content-muted">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/signup" className="mt-7 block">
                  <Button variant={tier.highlighted ? 'primary' : 'secondary'} className="w-full">
                    {tier.cta}
                  </Button>
                </Link>
              </GlassCard>
            </motion.div>
          ))}
        </StaggerGroup>
      </section>

      {/* ---------------- Final CTA ---------------- */}
      <section className="relative px-4 py-24 sm:px-6">
        <Reveal className="mx-auto max-w-4xl">
          <GlassCard className="relative overflow-hidden p-10 text-center sm:p-16">
            <Aurora intensity={0.55} interactive={false} />
            <div className="relative z-10">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-5xl">
                Your next idea is <span className="text-flow">one prompt</span> away.
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-content-muted">
                Join Aether and turn words into images, music, and code — in seconds.
              </p>
              <Magnetic strength={0.5} className="mt-8">
                <Link to="/signup">
                  <Button size="lg" iconRight={ArrowRight}>
                    Create your account
                  </Button>
                </Link>
              </Magnetic>
            </div>
          </GlassCard>
        </Reveal>
      </section>

      {/* ---------------- Footer ---------------- */}
      <footer className="border-t border-border/60 px-4 py-10 sm:px-6">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 sm:flex-row">
          <Logo />
          <p className="text-sm text-content-subtle">
            © {new Date().getFullYear()} Aether. Built on open models.
          </p>
          <div className="flex gap-5 text-sm text-content-muted">
            <a href="#features" className="hover:text-content">Features</a>
            <a href="#pricing" className="hover:text-content">Pricing</a>
            <Link to="/login" className="hover:text-content">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
